/**
 * HealthContours audience marketing content.
 *
 * One entry per anticipated HealthContours user — the eight canonical CareMap
 * viewer roles stored as `users.careMapRole` (see
 * `shared/utils/constants/careMapRoles.js`). Roles are NOT invented here; each
 * audience is keyed to a real `CareMapRole` and its authoritative label.
 *
 * Every audience gets the same product — HealthContours does not gate data or
 * change price by role, and paywalls are not enforced. These pages differ only
 * in framing and use-cases. Copy stays honest to what is live today:
 *   - CMS Care Compare facility measures (overall star rating, OP-18b ED
 *     throughput, OP-22 left-without-being-seen)
 *   - CDC PLACES community health estimates and Census ACS demographics
 *   - Pearson correlation between any two live measures that share a level
 * Emerging layers (HRSA HPSA shortage designations, HCAHPS patient experience,
 * drive-time catchment) are described as expanding — never promised as present.
 *
 * Block schema consumed by HealthContoursAudiencePage:
 *   - section     { kicker?, title?, lede?[] }
 *   - featureGrid { kicker?, title?, lede?[], columns?, features:[{title, body}] }
 *   - steps       { kicker?, title?, lede?[], numbered?, steps:[{tag?, title, body}] }
 *   - callout     { title?, body[] }
 *
 * Edit copy here, not in the JSX.
 */

import {
  CARE_MAP_ROLES,
  getCareMapRoleLabel,
} from "shared/utils/constants/careMapRoles";

/** Slug ↔ canonical CareMapRole. Ordered to follow CARE_MAP_ROLES. */
export const HEALTHCONTOURS_AUDIENCE_ROLE_BY_SLUG = Object.freeze({
  "for-hospital-leaders": "hospital_administrator",
  "for-quality-safety": "quality_safety_officer",
  "for-ed-operations": "ed_director_ops",
  "for-ems": "ems_regional_dispatch",
  "for-population-health": "population_health_community_benefit",
  "for-researchers": "researcher_academic",
  "for-patients": "patient_consumer",
  "for-payers": "payer_insurer",
});

const ROLE_TO_SLUG = Object.freeze(
  Object.fromEntries(
    Object.entries(HEALTHCONTOURS_AUDIENCE_ROLE_BY_SLUG).map(([slug, role]) => [
      role,
      slug,
    ]),
  ),
);

/** Audience slugs in CARE_MAP_ROLES order (single source for nav + routes). */
export const HEALTHCONTOURS_AUDIENCE_SLUGS = Object.freeze(
  CARE_MAP_ROLES.map((role) => ROLE_TO_SLUG[role]).filter(Boolean),
);

const SHARED_FOOTER =
  "HealthContours provides informational data derived from public federal sources (CMS Care Compare, CDC PLACES, Census ACS, HRSA). It is not a substitute for professional, clinical, regulatory, or business decision-making. All audiences get the same product; role changes framing, not price or features.";

const CONTOUR_CALLOUT = {
  type: "callout",
  title: "What powers the map today",
  body: [
    "HealthContours plots live CMS Care Compare facility measures — overall star rating, OP-18b emergency-department throughput, and OP-22 left-without-being-seen — alongside CDC PLACES community-health estimates and Census ACS demographics.",
    "Pick any two live measures that share a level and the map draws dual contours and reports a Pearson correlation, so you can see how facility quality and community conditions move together. Additional federal layers — HRSA shortage-area designations, HCAHPS patient experience, and drive-time catchment — are expanding onto the map over time.",
  ],
};

const HOSPITAL_ADMINISTRATOR = {
  slug: "for-hospital-leaders",
  careMapRole: "hospital_administrator",
  navLabel: "Hospital Leaders",
  hero: {
    eyebrow: getCareMapRoleLabel("hospital_administrator"),
    title: "See your hospital in the context of the community it serves.",
    lede: [
      "HealthContours puts your facility's public quality measures on the same map as the demographics, health burden, and access conditions of the surrounding population — so market and quality conversations start from shared, sourced federal data instead of anecdote.",
    ],
  },
  blocks: [
    {
      type: "featureGrid",
      kicker: "Why leaders use it",
      title: "Quality and geography, side by side.",
      features: [
        {
          title: "Benchmark against nearby facilities",
          body: "Compare your CMS overall star rating and Care Compare throughput measures against other hospitals in your state — the same public numbers your board and community already see.",
        },
        {
          title: "Understand your catchment",
          body: "Overlay Census ACS income and poverty with CDC PLACES health burden across the counties around you, so service-line and outreach decisions reflect who actually lives there.",
        },
        {
          title: "Correlate quality with need",
          body: "Put a facility quality measure next to a community-need measure and read the Pearson correlation — a defensible starting point for where performance and population conditions line up.",
        },
        {
          title: "One sourced picture for the room",
          body: "Every value traces back to a named federal dataset, so strategy discussions rest on data your stakeholders can verify rather than a vendor's black box.",
        },
      ],
    },
    CONTOUR_CALLOUT,
  ],
  cta: {
    title: "Bring your market and quality data onto one map.",
    lede: "Open the live map and explore your facilities in context, or create a free HealthContours account to save your work.",
  },
  footer: SHARED_FOOTER,
};

const QUALITY_SAFETY_OFFICER = {
  slug: "for-quality-safety",
  careMapRole: "quality_safety_officer",
  navLabel: "Quality & Safety",
  hero: {
    eyebrow: getCareMapRoleLabel("quality_safety_officer"),
    title: "Ground your quality story in public, sourced measures.",
    lede: [
      "HealthContours surfaces the CMS Care Compare measures your program is already judged on — and places them against the community conditions that shape them — so you can see performance and context together, not in separate spreadsheets.",
    ],
  },
  blocks: [
    {
      type: "featureGrid",
      kicker: "For quality & patient safety",
      title: "The measures you report, on a map.",
      features: [
        {
          title: "Track the Care Compare measures",
          body: "Overall star rating, OP-18b ED throughput, and OP-22 left-without-being-seen are plotted from the same public CMS data used in national reporting.",
        },
        {
          title: "See peers in your state",
          body: "Compare your facility's public measures against nearby hospitals to frame internal targets against what regional peers are achieving.",
        },
        {
          title: "Context for the numbers",
          body: "Overlay CDC PLACES community-health estimates so a throughput or access measure can be read alongside the burden of the population it serves.",
        },
        {
          title: "Correlation, not conjecture",
          body: "The dual-contour Pearson readout gives a transparent, reproducible way to describe how two public measures relate across facilities or geographies.",
        },
      ],
    },
    CONTOUR_CALLOUT,
  ],
  cta: {
    title: "Put your public quality measures in context.",
    lede: "Explore the live map, or create a free account to keep the comparisons you build.",
  },
  footer: SHARED_FOOTER,
};

const ED_DIRECTOR_OPS = {
  slug: "for-ed-operations",
  careMapRole: "ed_director_ops",
  navLabel: "ED & Operations",
  hero: {
    eyebrow: getCareMapRoleLabel("ed_director_ops"),
    title: "Throughput measures and the demand around your department.",
    lede: [
      "HealthContours maps the emergency-department measures CMS already publishes — throughput and left-without-being-seen — next to the demographics and health burden of the population your ED draws from.",
    ],
  },
  blocks: [
    {
      type: "featureGrid",
      kicker: "For ED directors & hospital ops",
      title: "Operational context at a glance.",
      features: [
        {
          title: "OP-18b and OP-22 on the map",
          body: "Emergency-department throughput and left-without-being-seen are plotted directly from public CMS Care Compare data for your facility and nearby EDs.",
        },
        {
          title: "Compare to neighboring EDs",
          body: "See how your department's public measures sit relative to other emergency departments in the state — useful framing for capacity and staffing conversations.",
        },
        {
          title: "Read demand from demographics",
          body: "Layer Census ACS and CDC PLACES to understand the insurance mix, chronic-disease burden, and access conditions of the communities that arrive at your door.",
        },
        {
          title: "Spot where throughput tracks need",
          body: "Correlate a throughput measure with a community-need measure to see, transparently, where operational pressure and population conditions align.",
        },
      ],
    },
    CONTOUR_CALLOUT,
  ],
  cta: {
    title: "See your ED's measures against real community demand.",
    lede: "Open the live map, or create a free account to save the views you rely on.",
  },
  footer: SHARED_FOOTER,
};

const EMS_REGIONAL_DISPATCH = {
  slug: "for-ems",
  careMapRole: "ems_regional_dispatch",
  navLabel: "EMS & Dispatch",
  hero: {
    eyebrow: getCareMapRoleLabel("ems_regional_dispatch"),
    title: "Know the destinations across your whole region.",
    lede: [
      "HealthContours shows every hospital in a state on one map with its public CMS quality measures — a regional view to inform destination awareness and planning alongside your own protocols.",
    ],
  },
  blocks: [
    {
      type: "featureGrid",
      kicker: "For EMS & regional dispatch",
      title: "A regional destination picture.",
      features: [
        {
          title: "Every facility, one map",
          body: "Emergency departments across the state appear together with their public CMS Care Compare measures — a shared reference for regional planning.",
        },
        {
          title: "Throughput at a glance",
          body: "OP-18b throughput and OP-22 left-without-being-seen help characterize how facilities across the region are performing on public measures.",
        },
        {
          title: "Community conditions by area",
          body: "CDC PLACES and Census ACS layers describe the health burden and demographics of the areas your services cover.",
        },
        {
          title: "Drive-time catchment is expanding",
          body: "Catchment and travel-time views are being built onto the map to make regional coverage even clearer over time.",
        },
      ],
    },
    CONTOUR_CALLOUT,
  ],
  cta: {
    title: "Get a regional view of destinations and demand.",
    lede: "Explore the live map, or create a free account to return to your planning views.",
  },
  footer: SHARED_FOOTER,
};

const POPULATION_HEALTH = {
  slug: "for-population-health",
  careMapRole: "population_health_community_benefit",
  navLabel: "Population Health",
  hero: {
    eyebrow: getCareMapRoleLabel("population_health_community_benefit"),
    title: "Match facility quality to the communities that need it most.",
    lede: [
      "HealthContours brings CDC PLACES health burden, Census ACS demographics, and CMS facility quality onto one map — the working surface for community-benefit and needs-assessment analysis, all traceable to public sources.",
    ],
  },
  blocks: [
    {
      type: "featureGrid",
      kicker: "For population health & community benefit",
      title: "Need and supply on the same canvas.",
      features: [
        {
          title: "Community health burden",
          body: "CDC PLACES county estimates — uninsured rate, diabetes, obesity, coronary heart disease, stroke, COPD, depression, smoking — mapped where your populations live.",
        },
        {
          title: "Demographic context",
          body: "Census ACS poverty rate and median income overlay the same geographies, so equity questions are grounded in sourced numbers.",
        },
        {
          title: "Facility quality nearby",
          body: "See the CMS public quality measures of the hospitals serving each area, connecting community need to available care.",
        },
        {
          title: "Correlate need with quality",
          body: "The dual-contour Pearson readout gives a transparent way to describe relationships between community conditions and facility performance for a CHNA or community-benefit narrative.",
        },
      ],
    },
    CONTOUR_CALLOUT,
  ],
  cta: {
    title: "Build your community picture from public data.",
    lede: "Open the live map, or create a free account to save the geographies you study.",
  },
  footer: SHARED_FOOTER,
};

const RESEARCHER_ACADEMIC = {
  slug: "for-researchers",
  careMapRole: "researcher_academic",
  navLabel: "Researchers",
  hero: {
    eyebrow: getCareMapRoleLabel("researcher_academic"),
    title: "Explore public health-system data before you commit to a pull.",
    lede: [
      "HealthContours is a fast, visual way to scan CMS Care Compare, CDC PLACES, and Census ACS together — to form hypotheses about facility quality and community conditions with every value traceable to its federal source.",
    ],
  },
  blocks: [
    {
      type: "featureGrid",
      kicker: "For researchers & academics",
      title: "A traceable exploratory surface.",
      features: [
        {
          title: "Multiple federal datasets, one view",
          body: "CMS facility quality, CDC PLACES community health, and Census ACS demographics are aligned geographically so you can explore relationships visually.",
        },
        {
          title: "Transparent provenance",
          body: "Each measure names the public dataset it comes from, so exploratory findings are grounded and easy to cite when you move to a formal source pull.",
        },
        {
          title: "Correlation as a starting point",
          body: "The Pearson readout across facilities or geographies offers a quick, honest signal — a hypothesis generator, not a substitute for rigorous analysis.",
        },
        {
          title: "No fabricated values",
          body: "Measures that are not yet live are labeled as coming, never filled with synthetic data — so what you see reflects real public sources only.",
        },
      ],
    },
    CONTOUR_CALLOUT,
  ],
  cta: {
    title: "Scan the public landscape before your deep dive.",
    lede: "Explore the live map, or create a free account to keep the views you build.",
  },
  footer: SHARED_FOOTER,
};

const PATIENT_CONSUMER = {
  slug: "for-patients",
  careMapRole: "patient_consumer",
  navLabel: "Patients",
  hero: {
    eyebrow: getCareMapRoleLabel("patient_consumer"),
    title: "See how hospitals near you compare — using public data.",
    lede: [
      "HealthContours puts the same public hospital quality ratings the federal government publishes onto a simple map, alongside a picture of the community around each facility. No account needed to look.",
    ],
  },
  blocks: [
    {
      type: "featureGrid",
      kicker: "For patients & consumers",
      title: "Public quality information, made visual.",
      features: [
        {
          title: "Star ratings you can trust",
          body: "Hospitals show their official CMS overall star rating — the same measure published on the government's Care Compare site — right on the map.",
        },
        {
          title: "Emergency-department signals",
          body: "See public measures of how emergency departments handle throughput and how often people leave before being seen, to help set expectations.",
        },
        {
          title: "Your community in context",
          body: "Community-health information from the CDC and demographics from the Census help you understand the area a hospital serves.",
        },
        {
          title: "Free to explore",
          body: "The map is open to everyone. Create a free account only if you want to save what you find.",
        },
      ],
    },
    {
      type: "callout",
      title: "Informational, not medical advice",
      body: [
        "HealthContours shows public federal data to help you compare and understand facilities. It is informational and is not a substitute for professional or clinical advice — always talk to a clinician about your care.",
      ],
    },
  ],
  cta: {
    title: "Look up hospitals near you.",
    lede: "Open the map — no account required — or create a free account to save the facilities you're comparing.",
  },
  footer: SHARED_FOOTER,
};

const PAYER_INSURER = {
  slug: "for-payers",
  careMapRole: "payer_insurer",
  navLabel: "Payers",
  hero: {
    eyebrow: getCareMapRoleLabel("payer_insurer"),
    title: "Network quality and community context on one map.",
    lede: [
      "HealthContours aligns public CMS facility quality with CDC PLACES health burden and Census ACS demographics across a state — a sourced, geographic view to inform network and market analysis.",
    ],
  },
  blocks: [
    {
      type: "featureGrid",
      kicker: "For payers & insurers",
      title: "Quality, geography, and population together.",
      features: [
        {
          title: "Facility quality across a market",
          body: "CMS overall star rating and Care Compare throughput measures for every hospital in a state, from the same public data used in national reporting.",
        },
        {
          title: "Population conditions by area",
          body: "CDC PLACES health burden and Census ACS demographics describe the members and communities behind each geography.",
        },
        {
          title: "Correlate quality with need",
          body: "The dual-contour Pearson readout offers a transparent way to explore how facility quality relates to community conditions across a market.",
        },
        {
          title: "Sourced and verifiable",
          body: "Every value traces to a named federal dataset — useful when analysis has to stand up to internal and external scrutiny.",
        },
      ],
    },
    CONTOUR_CALLOUT,
  ],
  cta: {
    title: "Explore quality and community across your markets.",
    lede: "Open the live map, or create a free account to save the market views you build.",
  },
  footer: SHARED_FOOTER,
};

/** Audience content keyed by slug. */
export const HEALTHCONTOURS_AUDIENCES = Object.freeze({
  "for-hospital-leaders": HOSPITAL_ADMINISTRATOR,
  "for-quality-safety": QUALITY_SAFETY_OFFICER,
  "for-ed-operations": ED_DIRECTOR_OPS,
  "for-ems": EMS_REGIONAL_DISPATCH,
  "for-population-health": POPULATION_HEALTH,
  "for-researchers": RESEARCHER_ACADEMIC,
  "for-patients": PATIENT_CONSUMER,
  "for-payers": PAYER_INSURER,
});

/**
 * @param {string} slug
 * @returns {object | null}
 */
export function getHealthContoursAudience(slug) {
  return HEALTHCONTOURS_AUDIENCES[slug] || null;
}

export default HEALTHCONTOURS_AUDIENCES;
